import React, { useCallback, useState } from "react";

import { useParams } from "react-router-dom";

import CompellonApi from "apis/compellon";
import { PageLoader } from "components/Common/clearsense-ui/components";
import { snakeCaseToTitleCase } from "components/DataIngestion/SourceDatabases/utils";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import ActionHeader from "./ActionHeader";
import { DEFAULT_PAGE_NUMBER, MINIMUM_PAGE_SIZE } from "./constants";
import PrimaryHeader from "./PrimaryHeader";
import Table from "./Table";

import { LOCAL_DATASET, RENAL_DATASET } from "../../../../Datasets/constants";
import NewAnalysis from "../New";
import { getTargetLabel } from "../utils";

const AnalysesHome = () => {
  const { datasetName } = useParams();

  const [filterConditions, setFilterConditions] = useState([]);
  const [datasetParams, setDatasetParams] = useState(
    datasetName ? LOCAL_DATASET(datasetName) : RENAL_DATASET
  );
  const [analyses, setAnalyses] = useState([]);
  const [isNewPaneOpen, setIsNewPaneOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || DEFAULT_PAGE_NUMBER;
      const pageSize = params.get("per_page") || MINIMUM_PAGE_SIZE;
      const payload = {
        ...datasetParams,
        per_page: pageSize,
        page: currentPage,
        search,
        job_type: "binary_model_build",
      };
      const {
        data: { job_list, job_count },
      } = await CompellonApi.fetchJobList(payload);
      setAnalyses({ count: job_count, data: handleJobList(job_list) });
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [datasetParams]);

  const {
    search,
    currentPage,
    pageSize,
    setSearch,
    setCurrentPage,
    setPageSize,
    debouncedSearchTerm,
  } = useSearchAndPaginate(
    fetchJobs,
    {},
    DEFAULT_PAGE_NUMBER,
    MINIMUM_PAGE_SIZE
  );

  const handleJobList = jobs => {
    const jobDetails = jobs.map(job => ({
      status: job.status,
      id: job.id,
      name: job?.name || `Analysis ${job.id}`,
      target: job?.job_settings?.target
        ? getTargetLabel(job.job_settings.target)
        : "Not available",
      runDate: job?.start_time,
      analysisType: snakeCaseToTitleCase(job.job_type) ?? "Analysis",
    }));

    return jobDetails;
  };

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <PrimaryHeader setIsNewPaneOpen={setIsNewPaneOpen} />
      <ActionHeader
        filterConditions={filterConditions}
        setFilterConditions={setFilterConditions}
        search={search}
        setSearch={setSearch}
        totalCount={analyses?.count}
      />
      {isLoading ? (
        <PageLoader />
      ) : (
        <Table
          analyses={analyses}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          debouncedSearchTerm={debouncedSearchTerm}
          setIsNewPaneOpen={setIsNewPaneOpen}
        />
      )}
      <NewAnalysis
        setIsNewPaneOpen={setIsNewPaneOpen}
        isNewPaneOpen={isNewPaneOpen}
        fetchJobs={fetchJobs}
        datasetParams={datasetParams}
        setDatasetParams={setDatasetParams}
      />
    </div>
  );
};

export default AnalysesHome;
